/**
 * Chat Debug Explorer — a dev/admin-only inspector docked to the bottom of the
 * chat (Chrome DevTools style) with a fullscreen toggle.
 *
 * Layout: master/detail. The master is grouped by TURN; each turn expands to
 * reveal its parts in order — reasoning, tool calls, text — plus step dividers.
 * Selecting any leaf shows its full structured detail (tool input/output as a
 * collapsible JSON tree) on the right.
 *
 * All content is derived client-side from the `/chat/:id/debug` report; tool
 * I/O comes from the (untruncated) message parts, enriched with trace timing by
 * matching `toolCallId`.
 */

import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Bug,
  ChevronRight,
  Download,
  Maximize2,
  Minimize2,
  RefreshCw,
  X,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/ui/markdown";
import { cn } from "@/lib/utils";
import { JsonView } from "./json-view";
import {
  type ChatDebugReport,
  type DebugCompaction,
  type TokenStats,
  useChatDebug,
} from "./use-chat-debug";

interface DebugExplorerPanelProps {
  chatId: string;
  isOpen: boolean;
  onClose: () => void;
}

/* ----------------------------- formatting ------------------------------ */

const num = (n: number | null | undefined) => (n ?? 0).toLocaleString();
const cost = (c: number | null | undefined) =>
  `${(c ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 })} PCU`;
const dur = (ms: number | null | undefined) =>
  ms == null ? "—" : ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
const time = (iso: string | null | undefined) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(undefined, { hour12: false }) +
        "." +
        String(d.getMilliseconds()).padStart(3, "0").slice(0, 2);
};

/* ------------------------------ tree model ----------------------------- */

type ToolStatus = "ok" | "err" | "pending";

type Leaf =
  | { kind: "step"; id: string; n: number }
  | { kind: "reasoning"; id: string; text: string; state?: string }
  | { kind: "text"; id: string; text: string; state?: string }
  | {
      kind: "tool";
      id: string;
      name: string;
      toolCallId?: string;
      partType: string;
      state?: string;
      status: ToolStatus;
      input: unknown;
      output: unknown;
      errorText?: string;
      durationMs: number | null;
    }
  | { kind: "file"; id: string; part: unknown };

type TurnNode = {
  type: "turn";
  id: string;
  index: number;
  model: string | null;
  tokens: TokenStats | null;
  costPcu: number;
  durationMs: number | null;
  hasError: boolean;
  ts: string | null;
  leaves: Leaf[];
};
type UserNode = { type: "user"; id: string; text: string; ts: string | null };
type CompactionNode = { type: "compaction"; id: string; data: DebugCompaction };
type TreeNode = TurnNode | UserNode | CompactionNode;

/** What the detail pane is showing — a turn leaf or a compaction event. */
type Selection = Leaf | { kind: "compaction"; id: string; data: DebugCompaction };

const partText = (parts: unknown): string =>
  Array.isArray(parts)
    ? parts
        .filter((p: any) => p?.type === "text")
        .map((p: any) => p.text)
        .join("\n")
        .trim()
    : "";

function buildTree(report: ChatDebugReport): TreeNode[] {
  // tool duration by toolCallId, harvested from trace events (when present)
  const durByCall: Record<string, number | null> = {};
  report.timeline.forEach((t) =>
    t.events.forEach((e) => {
      if (e.toolCallId && e.durationMs != null) durByCall[e.toolCallId] = e.durationMs;
    }),
  );

  // Compaction events are anchored to a visible message id; interleave them
  // into the stream right after their anchor so the divider lands in context.
  const compactionsByAnchor = new Map<string, DebugCompaction[]>();
  for (const c of report.compactions) {
    if (c.afterMessageId) {
      const arr = compactionsByAnchor.get(c.afterMessageId) ?? [];
      arr.push(c);
      compactionsByAnchor.set(c.afterMessageId, arr);
    }
  }
  const usedCompactions = new Set<string>();
  const nodes: TreeNode[] = [];
  let turnIndex = 0;

  const pushAnchoredCompactions = (messageId: string) => {
    const anchored = compactionsByAnchor.get(messageId);
    if (!anchored) return;
    for (const c of anchored) {
      nodes.push({ type: "compaction", id: c.id, data: c });
      usedCompactions.add(c.id);
    }
  };

  for (const m of report.messages) {
    if (m.role === "user") {
      nodes.push({ type: "user", id: m.id, text: partText(m.parts), ts: m.createdAt });
      pushAnchoredCompactions(m.id);
      continue;
    }
    if (m.role !== "assistant") continue;

    const parts = Array.isArray(m.parts) ? (m.parts as any[]) : [];
    const leaves: Leaf[] = [];
    let step = 0;
    parts.forEach((p, idx) => {
      const id = `${m.id}-${idx}`;
      const type = String(p?.type ?? "");
      if (type === "step-start") {
        step += 1;
        leaves.push({ kind: "step", id, n: step });
      } else if (type === "reasoning") {
        leaves.push({ kind: "reasoning", id, text: p.text ?? p.reasoning ?? "", state: p.state });
      } else if (type === "text") {
        leaves.push({ kind: "text", id, text: p.text ?? "", state: p.state });
      } else if (type.startsWith("tool-")) {
        const status: ToolStatus =
          p.state === "output-error" ? "err" : p.state === "output-available" ? "ok" : "pending";
        leaves.push({
          kind: "tool",
          id,
          name: type.slice(5),
          toolCallId: p.toolCallId,
          partType: type,
          state: p.state,
          status,
          input: p.input ?? null,
          output: p.output ?? null,
          errorText: p.errorText,
          durationMs: p.toolCallId ? durByCall[p.toolCallId] ?? null : null,
        });
      } else if (type === "file" || type.startsWith("source")) {
        leaves.push({ kind: "file", id, part: p });
      }
    });

    const tl = report.timeline[turnIndex];
    nodes.push({
      type: "turn",
      id: m.id,
      index: turnIndex,
      model: m.model ?? tl?.model ?? null,
      tokens: m.tokens ?? tl?.tokens ?? null,
      costPcu: tl?.costPcu ?? 0,
      durationMs: tl?.durationMs ?? null,
      hasError: leaves.some((l) => l.kind === "tool" && l.status === "err"),
      ts: m.createdAt,
      leaves,
    });
    pushAnchoredCompactions(m.id);
    turnIndex += 1;
  }

  // Compactions whose anchor message isn't in the list (older/pruned) — append
  // so they're never silently dropped.
  for (const c of report.compactions) {
    if (!usedCompactions.has(c.id)) nodes.push({ type: "compaction", id: c.id, data: c });
  }
  return nodes;
}

/* ------------------------------- badges -------------------------------- */

function Badge({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "model" | "tok" | "cost" | "time" | "ok" | "err" | "warn";
}) {
  const tones: Record<string, string> = {
    muted: "border-border text-muted-foreground",
    model: "border-purple-400/30 text-purple-600 dark:text-purple-300 bg-purple-500/5",
    tok: "border-blue-400/30 text-blue-600 dark:text-blue-300 bg-blue-500/5",
    cost: "border-green-400/30 text-green-600 dark:text-green-300 bg-green-500/5",
    time: "border-amber-400/30 text-amber-600 dark:text-amber-300 bg-amber-500/5",
    ok: "border-green-400/40 text-green-600 dark:text-green-300 bg-green-500/10",
    err: "border-red-400/40 text-red-600 dark:text-red-300 bg-red-500/10",
    warn: "border-amber-400/40 text-amber-700 dark:text-amber-300 bg-amber-500/10",
  };
  return (
    <span
      className={cn(
        "whitespace-nowrap rounded-full border px-1.5 py-0.5 font-mono text-[10px] leading-tight",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

const LEAF_ICON: Record<string, string> = {
  reasoning: "💭",
  tool: "⚙",
  text: "💬",
  file: "📎",
};
function toolStatusBadge(s: ToolStatus) {
  if (s === "ok") return <Badge tone="ok">✓</Badge>;
  if (s === "err") return <Badge tone="err">✗ error</Badge>;
  return <Badge tone="warn">pending</Badge>;
}

/* ------------------------------ master rail ---------------------------- */

type Filter = "all" | "tools" | "errors";

function LeafRow({
  leaf,
  selected,
  onSelect,
}: {
  leaf: Leaf;
  selected: boolean;
  onSelect: () => void;
}) {
  if (leaf.kind === "step") {
    return (
      <div className="flex items-center gap-2 px-3 py-1 pl-9">
        <span className="font-mono text-[9.5px] uppercase tracking-wide text-muted-foreground/60">
          step {leaf.n}
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
    );
  }
  const preview =
    leaf.kind === "tool"
      ? leaf.errorText ?? (leaf.input ? JSON.stringify(leaf.input) : "")
      : leaf.kind === "file"
        ? "file / source part"
        : (leaf as { text: string }).text;
  return (
    <button
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-2 px-3 py-1.5 pl-9 text-left hover:bg-muted/40",
        selected && "bg-blue-500/10 shadow-[inset_2px_0_0_var(--tw-shadow-color)] shadow-blue-500",
      )}
    >
      <span className="pt-0.5 text-[13px] leading-none">{LEAF_ICON[leaf.kind]}</span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="font-mono text-[12px]">
            {leaf.kind === "tool" ? leaf.name : leaf.kind}
          </span>
          {leaf.kind === "tool" && toolStatusBadge(leaf.status)}
          {leaf.kind === "tool" && leaf.durationMs != null && (
            <Badge tone="time">{dur(leaf.durationMs)}</Badge>
          )}
        </span>
        {preview && (
          <span className="mt-0.5 line-clamp-1 block font-mono text-[11px] text-muted-foreground">
            {preview}
          </span>
        )}
      </span>
    </button>
  );
}

function TurnGroup({
  turn,
  open,
  onToggle,
  filter,
  selectedId,
  onSelect,
}: {
  turn: TurnNode;
  open: boolean;
  onToggle: () => void;
  filter: Filter;
  selectedId: string | null;
  onSelect: (leaf: Leaf) => void;
}) {
  const leaves = turn.leaves.filter((l) => {
    if (filter === "all") return true;
    if (l.kind === "step") return false;
    if (filter === "tools") return l.kind === "tool";
    return l.kind === "tool" && l.status === "err";
  });
  const toolCount = turn.leaves.filter((l) => l.kind === "tool").length;
  return (
    <div className="border-b border-border">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-2.5 py-2 text-left hover:bg-muted/40"
      >
        <ChevronRight
          className={cn("size-3 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")}
        />
        <span className="text-[12.5px] font-semibold">Turn {turn.index + 1}</span>
        {turn.hasError && <Badge tone="err">error</Badge>}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
          {toolCount > 0 && <Badge tone="muted">{toolCount} tools</Badge>}
          {turn.model && <Badge tone="model">{turn.model}</Badge>}
          {turn.durationMs != null && <Badge tone="time">{dur(turn.durationMs)}</Badge>}
          {turn.tokens && <Badge tone="tok">{num(turn.tokens.total)} tok</Badge>}
          {turn.costPcu > 0 && <Badge tone="cost">{cost(turn.costPcu)}</Badge>}
        </div>
      </button>
      {open &&
        (leaves.length === 0 ? (
          <div className="px-3 py-1.5 pl-9 font-mono text-[11px] text-muted-foreground/70">
            {filter === "all" ? "no parts" : `no ${filter}`}
          </div>
        ) : (
          leaves.map((leaf) => (
            <LeafRow
              key={leaf.id}
              leaf={leaf}
              selected={selectedId === leaf.id}
              onSelect={() => onSelect(leaf)}
            />
          ))
        ))}
    </div>
  );
}

/* ------------------------------ detail pane ---------------------------- */

function ToolDetail({ leaf }: { leaf: Extract<Leaf, { kind: "tool" }> }) {
  const [sub, setSub] = useState<"input" | "output" | "meta">("input");
  const meta = {
    toolCallId: leaf.toolCallId,
    partType: leaf.partType,
    state: leaf.state,
    status: leaf.status,
    durationMs: leaf.durationMs,
  };
  return (
    <>
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 font-mono text-[14px]">
          <span className="text-cyan-600 dark:text-cyan-400">⚙</span>
          {leaf.name}
          {toolStatusBadge(leaf.status)}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {leaf.durationMs != null && <Badge tone="time">{dur(leaf.durationMs)}</Badge>}
          {leaf.toolCallId && <Badge tone="muted">{leaf.toolCallId}</Badge>}
        </div>
      </div>
      <div className="flex gap-1.5 border-b border-border px-3 py-2">
        {(["input", "output", "meta"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSub(s)}
            className={cn(
              "rounded-md border px-2.5 py-1 text-[11px] capitalize",
              sub === s
                ? "border-blue-500 bg-blue-500/10 text-foreground"
                : "border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {s}
            {s === "output" && leaf.status === "err" ? " ⚠" : ""}
          </button>
        ))}
      </div>
      <div className="p-3">
        {sub === "input" && <JsonView data={leaf.input} />}
        {sub === "output" &&
          (leaf.status === "err" ? (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 font-mono text-[12px] text-red-600 dark:text-red-300">
              {leaf.errorText ?? "Tool returned an error."}
            </div>
          ) : (
            <JsonView data={leaf.output} />
          ))}
        {sub === "meta" && <JsonView data={meta} />}
      </div>
    </>
  );
}

function TextDetail({
  leaf,
}: {
  leaf: Extract<Leaf, { kind: "reasoning" | "text" }>;
}) {
  // Assistant text is markdown — show the rendered preview by default, with a
  // toggle to the raw source.
  const [view, setView] = useState<"preview" | "source">("preview");
  return (
    <>
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 font-mono text-[14px]">
          <span>{LEAF_ICON[leaf.kind]}</span>
          {leaf.kind}
          {leaf.state && <Badge tone="muted">{leaf.state}</Badge>}
          <div className="ml-auto flex gap-1">
            {(["preview", "source"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-[11px] capitalize",
                  view === v
                    ? "border-blue-500 bg-blue-500/10 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="p-3">
        {!leaf.text ? (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-[13px] text-muted-foreground">
            (empty)
          </div>
        ) : view === "preview" ? (
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <Markdown>{leaf.text}</Markdown>
          </div>
        ) : (
          <pre className="overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 font-mono text-[12px] leading-relaxed text-foreground">
            {leaf.text}
          </pre>
        )}
      </div>
    </>
  );
}

function CompactionBand({
  c,
  selected,
  onSelect,
}: {
  c: DebugCompaction;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-2 border-b border-dashed border-amber-400/50 bg-amber-500/5 px-2.5 py-2 text-left hover:bg-amber-500/10",
        selected && "bg-amber-500/15",
      )}
    >
      <span className="text-amber-600 dark:text-amber-400">⌁</span>
      <span className="text-[12.5px] font-semibold text-amber-700 dark:text-amber-300">
        Compaction
      </span>
      <span className="text-[11px] text-muted-foreground">
        {c.messageCount} msgs collapsed
      </span>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
        <Badge tone="tok">
          {num(c.beforeTokens)} → {num(c.afterTokens)} tok
        </Badge>
        {c.savedTokens > 0 && (
          <Badge tone="cost">
            −{num(c.savedTokens)} ({Math.round(c.reductionPct * 100)}%)
          </Badge>
        )}
        {c.estimated && <Badge tone="warn">~est</Badge>}
      </div>
    </button>
  );
}

function CompactionStat({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: string }) {
  return (
    <div className={cn("flex-1 rounded-md border p-3 text-center", tone)}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground/70">{label}</div>
      <div className="font-mono text-lg">{value}</div>
      <div className="text-[10px] text-muted-foreground">{sub}</div>
    </div>
  );
}

function CompactionDetail({ c }: { c: DebugCompaction }) {
  return (
    <>
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-2 font-mono text-[14px]">
          <span className="text-amber-600 dark:text-amber-400">⌁</span>
          Context compaction
          {c.estimated && <Badge tone="warn">estimated</Badge>}
        </div>
        {c.createdAt && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge tone="muted">{time(c.createdAt)}</Badge>
          </div>
        )}
      </div>
      <div className="space-y-4 p-4">
        <div className="flex items-stretch gap-2">
          <CompactionStat
            label="Before"
            value={num(c.beforeTokens)}
            sub="tokens collapsed"
            tone="border-border bg-muted/30 text-blue-600 dark:text-blue-400"
          />
          <div className="flex items-center text-muted-foreground">→</div>
          <CompactionStat
            label="After"
            value={num(c.afterTokens)}
            sub="summary tokens"
            tone="border-border bg-muted/30 text-purple-600 dark:text-purple-400"
          />
          <div className="flex items-center text-muted-foreground">=</div>
          <CompactionStat
            label="Saved"
            value={num(c.savedTokens)}
            sub={`${Math.round(c.reductionPct * 100)}% reduction`}
            tone="border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400"
          />
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
          <KvRow k="Messages collapsed" v={String(c.messageCount)} />
          {c.keptCount != null && <KvRow k="Messages kept" v={String(c.keptCount)} />}
          <KvRow k="Anchor message" v={c.afterMessageId ?? "—"} mono />
          {c.tailStartMessageId && <KvRow k="Tail starts at" v={c.tailStartMessageId} mono />}
        </div>

        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Summary that replaced the collapsed messages
          </div>
          <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 text-[13px] leading-relaxed text-foreground">
            {c.summary || (
              <span className="text-muted-foreground">
                (empty summary — this compaction produced no text)
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function KvRow({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] text-muted-foreground/70">{k}</span>
      <span className={cn("text-[12px]", mono && "break-all font-mono")}>{v}</span>
    </div>
  );
}

/* -------------------------------- panel -------------------------------- */

type Tab = "turns" | "raw";

export function DebugExplorerPanel({ chatId, isOpen, onClose }: DebugExplorerPanelProps) {
  const { data: report, isLoading, isError, error, refetch, isFetching } = useChatDebug(
    chatId,
    isOpen,
  );

  const [tab, setTab] = useState<Tab>("turns");
  const [fullscreen, setFullscreen] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [openTurns, setOpenTurns] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<Selection | null>(null);

  // resizable dock height (only used when not fullscreen)
  const [height, setHeight] = useState(440);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);
  const onDragStart = (e: React.MouseEvent) => {
    dragRef.current = { startY: e.clientY, startH: height };
    const move = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const next = dragRef.current.startH + (dragRef.current.startY - ev.clientY);
      setHeight(Math.max(220, Math.min(window.innerHeight - 60, next)));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const tree = useMemo(() => (report ? buildTree(report) : []), [report]);

  // default-open the first turn once data lands
  const firstTurnId = useMemo(
    () => tree.find((n): n is TurnNode => n.type === "turn")?.id,
    [tree],
  );
  const isTurnOpen = (id: string) =>
    openTurns[id] ?? (id === firstTurnId);

  const exportJson = useCallback(() => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chat-debug-${chatId.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Trace exported");
  }, [report, chatId]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="debug-dock"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          className={cn(
            "flex flex-col overflow-hidden border-t border-border bg-background shadow-[0_-12px_40px_rgba(0,0,0,0.4)]",
            // Docked spans the full screen width (left→right, like DevTools);
            // fullscreen covers the whole viewport.
            fullscreen ? "fixed inset-0 z-[60]" : "fixed inset-x-0 bottom-0 z-[60]",
          )}
          style={fullscreen ? undefined : { height }}
        >
          {/* drag handle */}
          {!fullscreen && (
            <div
              onMouseDown={onDragStart}
              className="group h-1.5 shrink-0 cursor-ns-resize"
            >
              <div className="mx-auto mt-0.5 h-0.5 w-10 rounded-full bg-border group-hover:bg-blue-500" />
            </div>
          )}

          {/* toolbar */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-1.5">
            <div className="flex size-6 items-center justify-center rounded-md bg-primary/10 text-primary">
              <Bug className="size-3.5" />
            </div>
            <div className="leading-tight">
              <div className="text-[12px] font-semibold">Chat Debug Explorer</div>
              <div className="font-mono text-[9.5px] text-muted-foreground">{chatId}</div>
            </div>

            <div className="ml-3 flex gap-0.5">
              {(["turns", "raw"] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-[12px] capitalize",
                    tab === t ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* summary stats */}
            {report && (
              <div className="ml-auto mr-1 flex items-center gap-3">
                <Stat k="Tokens" v={num(report.totals.tokens.total)} />
                <Stat k="Cost" v={cost(report.totals.costPcu)} g />
                <Stat k="Turns" v={String(report.totals.turns)} />
                <Stat k="Tools" v={String(report.totals.toolCalls)} />
                {report.totals.compactions > 0 && (
                  <Stat k="Compactions" v={String(report.totals.compactions)} />
                )}
                {report.totals.errors > 0 && <Stat k="Errors" v={String(report.totals.errors)} danger />}
              </div>
            )}

            <div className={cn("flex items-center gap-0.5", !report && "ml-auto")}>
              <IconBtn title="Refresh" onClick={() => refetch()}>
                <RefreshCw className={cn("size-3.5", isFetching && "animate-spin")} />
              </IconBtn>
              <IconBtn title="Export trace" onClick={exportJson} disabled={!report}>
                <Download className="size-3.5" />
              </IconBtn>
              <IconBtn
                title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={() => setFullscreen((f) => !f)}
              >
                {fullscreen ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
              </IconBtn>
              <IconBtn title="Close (⌘⇧D)" onClick={onClose}>
                <X className="size-3.5" />
              </IconBtn>
            </div>
          </div>

          {/* body */}
          <div className="flex min-h-0 flex-1">
            {isLoading && (
              <div className="flex w-full items-center justify-center text-sm text-muted-foreground">
                Loading debug report…
              </div>
            )}
            {isError && (
              <div className="flex w-full flex-col items-center justify-center gap-2 text-sm text-red-600 dark:text-red-400">
                <AlertTriangle className="size-5" />
                Failed to load: {(error as Error)?.message ?? "unknown error"}
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            )}

            {report && tab === "raw" && (
              <div className="min-h-0 w-full overflow-auto p-3">
                <JsonView data={report} className="text-[12px]" />
                {report.notes.length > 0 && <NotesBox notes={report.notes} />}
              </div>
            )}

            {report && tab === "turns" && (
              <>
                {/* master */}
                <div className="flex w-[44%] min-h-0 min-w-[300px] flex-col border-r border-border">
                  <div className="flex shrink-0 items-center gap-1.5 border-b border-border px-2.5 py-1.5">
                    {(["all", "tools", "errors"] as Filter[]).map((f) => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={cn(
                          "rounded-full border px-2 py-0.5 text-[11px] capitalize",
                          filter === f
                            ? "border-blue-500 bg-blue-500/10 text-foreground"
                            : "border-border text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                  <div className="flex-1 overflow-auto">
                    {tree.map((node) => {
                      if (node.type === "user") {
                        return (
                          <button
                            key={node.id}
                            onClick={() =>
                              setSelected({ kind: "text", id: node.id, text: node.text })
                            }
                            className={cn(
                              "flex w-full items-center gap-2 border-b border-border px-2.5 py-1.5 text-left hover:bg-muted/40",
                              selected?.id === node.id && "bg-blue-500/10",
                            )}
                          >
                            <span className="text-[12px]">▸</span>
                            <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                              user
                            </span>
                            <span className="line-clamp-1 flex-1 text-[12px] text-muted-foreground">
                              {node.text}
                            </span>
                            <span className="font-mono text-[10px] text-muted-foreground/60">
                              {time(node.ts)}
                            </span>
                          </button>
                        );
                      }
                      if (node.type === "compaction") {
                        // Compaction bands only show in the unfiltered view.
                        return filter === "all" ? (
                          <CompactionBand
                            key={node.id}
                            c={node.data}
                            selected={selected?.id === node.id}
                            onSelect={() =>
                              setSelected({ kind: "compaction", id: node.id, data: node.data })
                            }
                          />
                        ) : null;
                      }
                      return (
                        <TurnGroup
                          key={node.id}
                          turn={node}
                          open={isTurnOpen(node.id)}
                          onToggle={() =>
                            setOpenTurns((prev) => ({
                              ...prev,
                              [node.id]: !isTurnOpen(node.id),
                            }))
                          }
                          filter={filter}
                          selectedId={selected?.id ?? null}
                          onSelect={setSelected}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* detail */}
                <div className="min-h-0 min-w-0 flex-1 overflow-auto">
                  {!selected ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
                      <span>Select a turn's tool call or part to inspect →</span>
                      {report.notes.length > 0 && <NotesBox notes={report.notes} />}
                    </div>
                  ) : selected.kind === "tool" ? (
                    <ToolDetail leaf={selected} />
                  ) : selected.kind === "reasoning" || selected.kind === "text" ? (
                    <TextDetail leaf={selected} />
                  ) : selected.kind === "compaction" ? (
                    <CompactionDetail c={selected.data} />
                  ) : selected.kind === "file" ? (
                    <div className="p-3">
                      <JsonView data={selected.part} />
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ------------------------------ small bits ----------------------------- */

function IconBtn({
  children,
  title,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-7"
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
}

function Stat({ k, v, g, danger }: { k: string; v: string; g?: boolean; danger?: boolean }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[8.5px] uppercase tracking-wide text-muted-foreground/60">{k}</span>
      <span
        className={cn(
          "font-mono text-[12px]",
          g && "text-green-600 dark:text-green-400",
          danger && "text-red-600 dark:text-red-400",
        )}
      >
        {v}
      </span>
    </div>
  );
}

function NotesBox({ notes }: { notes: string[] }) {
  return (
    <div className="mt-3 max-w-xl rounded-md border border-border bg-muted/20 p-3 text-left">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Notes & known gaps
      </div>
      <ul className="space-y-1">
        {notes.map((n, i) => (
          <li key={i} className="text-[11px] text-muted-foreground">
            • {n}
          </li>
        ))}
      </ul>
    </div>
  );
}
